import { createAsyncThunk } from '@reduxjs/toolkit';
import moment from 'moment';
import { getProjectInvoiceStartDate } from 'scenes/ExpenseTracking/store/services/getProjectInvoiceStartDate';
import { summarizeUrakkaAssignments } from 'scenes/ExpenseTracking/utils/projectBudgetSummary';
import { getProjectAssignmentCosts } from 'store/assignments/services/getProjectAssignmentCosts';
import { selectCostsByAssignmentAndCategory } from 'store/invoices/selectors/invoices.selectors';
import { getProjectInvoicesByDate } from 'store/invoices/services/getProjectInvoicesByDate';
import { getProjectInvoicesByRowsByDate } from 'store/invoices/services/getProjectInvoicesByRowsByDate';
import {
  buildExpenseSeries,
  getInvoiceCostParts,
  getTopCostCategories,
} from '../utils/expenseChartData';

/** How long a computed summary is reused before the dashboard reloads it. */
export const EXPENSE_SUMMARY_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * The whole-project range the expense tracking page's "Koko ajalta" chip
 * uses: from the day of the project's first invoice (the start of 2000 when
 * it has none) to the end of this year.
 * @param {string|null} startDate the project's invoice start date
 * @returns {{fromDate: string, toDate: string}} YYYY-MM-DD
 */
export const getWholeProjectRange = (startDate, now = moment()) => ({
  fromDate: (startDate ? moment(startDate) : moment('2000-01-01'))
    .startOf('day')
    .format('YYYY-MM-DD'),
  toDate: moment(now).endOf('year').format('YYYY-MM-DD'),
});

const loadAllInvoices = async (projectId, fromDate, toDate) => {
  const invoices = [];
  let response;
  do {
    response = await getProjectInvoicesByDate(
      projectId,
      fromDate,
      toDate,
      response?.LastEvaluatedKey
    );
    invoices.push(...(response?.Items || []));
  } while (response?.LastEvaluatedKey);
  return invoices;
};

const loadAllInvoicesByRows = async (projectId, fromDate, toDate) => {
  const invoices = [];
  let response;
  do {
    response = await getProjectInvoicesByRowsByDate(
      projectId,
      fromDate,
      toDate,
      response?.LastEvaluatedKey
    );
    invoices.push(...(response?.Invoices || []));
  } while (response?.LastEvaluatedKey);
  return invoices;
};

/**
 * The project's cost figures for the overview's expense card, computed with
 * the expense tracking page's own rules — its littera selector's combiner and
 * summarizeUrakkaAssignments, the figures of the page's Urakka row — over the
 * page's whole-project range, so the card and the page agree.
 *
 * The data is fetched with the page's services and kept out of the page's
 * slices (only the summary is stored), so nothing the dashboard loads leaks
 * into the page. A summary younger than EXPENSE_SUMMARY_MAX_AGE_MS is reused.
 *
 * Resolves `{ fromDate, toDate, budget, actualCost, remaining, percent,
 * series, topCategories }`: `budget` is the littera budgets' total
 * (Budjetoitu), `actualCost` the realized cost (Toteutunut), `remaining`
 * budget − cost (0 without a budget, negative when over it) and `percent`
 * cost / budget × 100 (0 without one). For the dashboard's charts, derived
 * from the same entries (utils/expenseChartData) and small enough to keep:
 * `series` the invoices' cumulative cost, `{ bucket, points: [{ date,
 * cumulative }] }` (at most EXPENSE_SERIES_MAX_POINTS points; the work hours,
 * other costs and transfers have no day and are not on it), and
 * `topCategories` the top-level litteras the most was spent on, `[{ id, name,
 * cost }]` (at most EXPENSE_TOP_CATEGORY_COUNT, largest first).
 */
export const loadProjectExpenseSummary = createAsyncThunk(
  'projectOverview/loadExpenseSummary',
  async ({ projectId }) => {
    let startDate = null;
    try {
      startDate = await getProjectInvoiceStartDate(projectId);
    } catch (error) {
      startDate = null;
    }
    const { fromDate, toDate } = getWholeProjectRange(startDate);

    const [invoices, invoicesByRows, assignmentCosts] = await Promise.all([
      loadAllInvoices(projectId, fromDate, toDate),
      loadAllInvoicesByRows(projectId, fromDate, toDate),
      getProjectAssignmentCosts(projectId, fromDate, toDate),
    ]);

    // The shapes the page's slices hold: invoices and costs by id (the page
    // merges both invoice lists into one entity map).
    const invoicesById = {};
    [...invoices, ...invoicesByRows].forEach((invoice) => {
      if (invoice && invoice.id) invoicesById[invoice.id] = invoice;
    });
    const costsById = {};
    (assignmentCosts || []).forEach((assignment) => {
      if (assignment && assignment.id) costsById[assignment.id] = assignment;
    });

    // The page's own littera computation (no filters, no additional works:
    // the Urakka figures leave those out anyway).
    const entries = Object.values(
      selectCostsByAssignmentAndCategory.resultFunc(
        invoicesById,
        costsById,
        {},
        projectId,
        []
      )
    );
    const urakka = summarizeUrakkaAssignments(entries);

    return {
      fromDate,
      toDate,
      loadedAt: Date.now(),
      budget: urakka.finalBudget || 0,
      actualCost: urakka.actualCost || 0,
      remaining: urakka.kustannustoteumaLeft || 0,
      percent: urakka.kustannustoteumaSum || 0,
      series: buildExpenseSeries(getInvoiceCostParts(entries)),
      topCategories: getTopCostCategories(entries),
    };
  },
  {
    // Once per project at a time, and not again while fresh.
    condition: ({ projectId, force }, { getState }) => {
      const entry = getState().projectOverview?.expenseSummaries?.[projectId];
      if (!entry) return true;
      if (entry.status === 'loading') return false;
      if (force) return true;
      return !(
        entry.status === 'succeeded' &&
        Date.now() - entry.loadedAt < EXPENSE_SUMMARY_MAX_AGE_MS
      );
    },
  }
);
